import { useState } from "react";
import { Loader2, Pencil, Plus } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import { useToast } from "@/hooks/use-toast";
import { formatApiError } from "@/lib/api-error";
import { useAgencias } from "@/hooks/use-agencias";
import {
  useCreateInviteReceiptText,
  useInviteReceiptTexts,
  useUpdateInviteReceiptText,
} from "@/hooks/use-invite-receipt-texts";
import type { InviteReceiptText, InviteReceiptTextBody, OmieEmpresaChave } from "@/types/finance";

const ANY_COMPANY = "qualquer";
const ALL_AGENCIES = "todas";

const COMPANY_OPTIONS: Array<{ value: OmieEmpresaChave; label: string }> = [
  { value: "corisco", label: "Corisco" },
  { value: "faisca", label: "Faísca" },
  { value: "gota_serena", label: "Gota Serena" },
];

interface TextDraft {
  id: string | null;
  titulo: string;
  texto: string;
  agenciaId: string;
  empresa: string;
}

const EMPTY_DRAFT: TextDraft = { id: null, titulo: "", texto: "", agenciaId: ALL_AGENCIES, empresa: ANY_COMPANY };

function scopeLabel(text: InviteReceiptText): string {
  const parts = [text.agencia?.nome, text.empresa?.nome].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "Geral";
}

/**
 * Standard receipt texts ("orientação de faturamento") applied to a period's
 * invite. An agency with a text of its own requires every period to be
 * confirmed before the documents are generated.
 */
export function InviteTextsTab() {
  const { toast } = useToast();
  const [showInactive, setShowInactive] = useState(false);
  const [draft, setDraft] = useState<TextDraft | null>(null);
  const textsQuery = useInviteReceiptTexts({ incluir_inativos: showInactive });
  const { data: agencies = [] } = useAgencias();
  const createText = useCreateInviteReceiptText();
  const updateText = useUpdateInviteReceiptText();
  const saving = createText.isPending || updateText.isPending;

  const reportError = (error: unknown) => {
    const formatted = formatApiError(error);
    toast({ variant: "destructive", title: formatted.title, description: formatted.detail });
  };

  const startEditing = (text: InviteReceiptText) =>
    setDraft({
      id: text.id,
      titulo: text.titulo,
      texto: text.texto,
      agenciaId: text.agencia?.id ?? ALL_AGENCIES,
      empresa: text.empresa?.chave ?? ANY_COMPANY,
    });

  const save = async () => {
    if (!draft || !draft.titulo.trim() || !draft.texto.trim()) return;
    const body: InviteReceiptTextBody = {
      titulo: draft.titulo.trim(),
      texto: draft.texto.trim(),
      agencia_id: draft.agenciaId === ALL_AGENCIES ? null : draft.agenciaId,
      omie_empresa: draft.empresa === ANY_COMPANY ? null : (draft.empresa as OmieEmpresaChave),
    };
    try {
      if (draft.id) {
        await updateText.mutateAsync({ textId: draft.id, body });
        toast({ title: "Texto atualizado" });
      } else {
        await createText.mutateAsync(body);
        toast({ title: "Texto cadastrado" });
      }
      setDraft(null);
    } catch (error) {
      reportError(error);
    }
  };

  const toggleActive = async (text: InviteReceiptText) => {
    try {
      await updateText.mutateAsync({ textId: text.id, body: { ativo: !text.ativo } });
      toast({ title: text.ativo ? "Texto desativado" : "Texto reativado" });
    } catch (error) {
      reportError(error);
    }
  };

  const texts = textsQuery.data?.items ?? [];
  const agencyOptions = [
    { value: ALL_AGENCIES, label: "Todas as agências" },
    ...agencies.map((agency) => ({ value: agency.id, label: agency.nome })),
  ];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={showInactive} onCheckedChange={setShowInactive} />
          Mostrar desativados
        </label>
        <Button onClick={() => setDraft(EMPTY_DRAFT)} className="gap-1.5">
          <Plus className="h-4 w-4" />
          Novo texto
        </Button>
      </div>

      {textsQuery.isLoading ? (
        <LoadingState variant="list" count={4} />
      ) : textsQuery.isError ? (
        <ErrorState onRetry={() => textsQuery.refetch()} />
      ) : texts.length === 0 ? (
        <EmptyState
          title="Nenhum texto cadastrado"
          description="Cadastre a orientação de faturamento que cada agência usa. Ela vira opção no invite de cada período."
        />
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Título</TableHead>
                <TableHead>Texto</TableHead>
                <TableHead>Vale para</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {texts.map((text) => (
                <TableRow key={text.id} className={text.ativo ? undefined : "opacity-60"}>
                  <TableCell className="font-medium">
                    {text.titulo}
                    {!text.ativo && (
                      <Badge variant="outline" className="ml-2 text-[10px]">
                        Desativado
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="max-w-md whitespace-pre-line text-sm text-muted-foreground">{text.texto}</TableCell>
                  <TableCell className="text-sm">{scopeLabel(text)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button type="button" size="icon" variant="ghost" aria-label="Editar" onClick={() => startEditing(text)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={() => toggleActive(text)}>
                        {text.ativo ? "Desativar" : "Reativar"}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={!!draft} onOpenChange={(open) => !open && setDraft(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{draft?.id ? "Editar texto" : "Novo texto"}</DialogTitle>
            <DialogDescription>
              Aparece no invite no lugar da data de recebimento. Se a agência tiver um texto próprio, cada período
              precisa ser confirmado antes de gerar os documentos.
            </DialogDescription>
          </DialogHeader>
          {draft && (
            <div className="grid gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="texto-invite-titulo" className="text-xs font-medium text-muted-foreground">
                  Título
                </Label>
                <Input
                  id="texto-invite-titulo"
                  value={draft.titulo}
                  maxLength={120}
                  placeholder="Ex.: Nota até o dia 5"
                  onChange={(event) => setDraft({ ...draft, titulo: event.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="texto-invite-texto" className="text-xs font-medium text-muted-foreground">
                  Texto
                </Label>
                <Textarea
                  id="texto-invite-texto"
                  value={draft.texto}
                  maxLength={2000}
                  rows={3}
                  placeholder="Ex.: nota entre os dias 1 e 5 do mês seguinte, para recebimento no dia 20"
                  onChange={(event) => setDraft({ ...draft, texto: event.target.value })}
                />
                <p className="text-xs text-muted-foreground">
                  Escreva <code>{"{empresa}"}</code> para citar a empresa que fatura a agência no período (Corisco,
                  Faísca ou Gota Serena). Ex.: recebimento via {"{empresa}"}, cerca de 30 dias após a finalização.
                </p>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">Agência</Label>
                  <Combobox
                    options={agencyOptions}
                    value={draft.agenciaId}
                    onValueChange={(value) => setDraft({ ...draft, agenciaId: value || ALL_AGENCIES })}
                    allowCustomValue={false}
                    placeholder="Todas as agências"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground">Empresa</Label>
                  <Select value={draft.empresa} onValueChange={(value) => setDraft({ ...draft, empresa: value })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ANY_COMPANY}>Qualquer empresa</SelectItem>
                      {COMPANY_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDraft(null)}>
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={!draft?.titulo.trim() || !draft?.texto.trim() || saving}
              onClick={save}
              className="gap-1.5"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
